/**
 * 座位列表：牌桌之外的「名单视图」。
 *
 * 牌桌上的座位块（`table/Seat.tsx`）是按几何摆的，空座只画一个虚线框、
 * 也没有点击入口；等待开局和选座这两个场景需要的是一张**能读也能点**的清单：
 * 谁在、多少筹码、什么状态、哪些位置还空着。
 *
 * 纯展示组件：不读 store、不发消息，全部经 props。这样同一份 UI 能同时服务
 * 等待室（带踢人）和选座面板（带坐下），也能在 node 环境静态渲染做冒烟测试。
 */
import type { ReactNode } from 'react'
import type { PublicHand, PublicSeat, SeatIndex, TopUpRequestView } from '@poker/shared'

import { formatChips } from '../table/cardFormat.js'

import { seatBadges, seatPositionMark } from './roomDerive.js'
import './room.css'

export interface SeatListProps {
  /** 全部座位（含空座），一般直接传 `view.seats` */
  readonly seats: readonly PublicSeat[]
  /** 当前手牌；null = 还没开局，此时不显示 D/SB/BB 与行动状态 */
  readonly hand: PublicHand | null
  readonly heroSeat: SeatIndex | null
  /** 空座是否可点（自己已入座 / 是旁观者但房间不收人时为 false） */
  readonly canSit?: boolean
  readonly onSit?: (seat: SeatIndex) => void
  /** 空座按钮上的字：坐下（旁观者入座）/ 换过去（换座） */
  readonly sitLabel?: string
  /** 房主才有踢人入口 */
  readonly canKick?: boolean
  /** 给的是整行座位：AI 与真人移出发的是两条不同的消息（见 roomDerive 的 kickMessage） */
  readonly onKick?: (seat: PublicSeat) => void
  /** 隐藏空座（牌局进行中的名单不需要「坐下」入口） */
  readonly hideEmpty?: boolean
  /** 在途的买分申请（`view.topUps`）：筹码为 0 的人据此显示「买分中」还是「筹码用完」 */
  readonly topUps?: readonly TopUpRequestView[]
}

const NO_TOPUPS: readonly TopUpRequestView[] = []

export function SeatList({
  seats,
  hand,
  heroSeat,
  canSit = false,
  onSit,
  sitLabel = '坐下',
  canKick = false,
  onKick,
  hideEmpty = false,
  topUps = NO_TOPUPS,
}: SeatListProps): ReactNode {
  const rows = [...seats].sort((a, b) => a.index - b.index).filter((s) => !hideEmpty || s.occupant !== null)

  return (
    <ul className="seatlist" role="list">
      {rows.map((s) => (
        <SeatRow
          key={s.index}
          seat={s}
          hand={hand}
          topUps={topUps}
          isHero={s.index === heroSeat}
          canSit={canSit}
          sitLabel={sitLabel}
          {...(onSit === undefined ? {} : { onSit })}
          canKick={canKick}
          {...(onKick === undefined ? {} : { onKick })}
        />
      ))}
    </ul>
  )
}

interface SeatRowProps {
  readonly seat: PublicSeat
  readonly hand: PublicHand | null
  readonly topUps: readonly TopUpRequestView[]
  readonly isHero: boolean
  readonly canSit: boolean
  readonly sitLabel: string
  readonly onSit?: (seat: SeatIndex) => void
  readonly canKick: boolean
  readonly onKick?: (seat: PublicSeat) => void
}

function SeatRow({ seat, hand, topUps, isHero, canSit, sitLabel, onSit, canKick, onKick }: SeatRowProps): ReactNode {
  const mark = seatPositionMark(seat.index, hand)
  const badges = seatBadges(seat, hand, topUps)
  const occupant = seat.occupant

  if (occupant === null) {
    return (
      <li className="seatrow seatrow--empty">
        <span className="seatrow__no num">{seat.index + 1}</span>
        <span className="seatrow__name">空位</span>
        {canSit && onSit !== undefined ? (
          <button type="button" className="btn btn--sit" onClick={() => onSit(seat.index)}>
            {sitLabel}
          </button>
        ) : (
          <span className="seatrow__hint">—</span>
        )}
      </li>
    )
  }

  const classes = ['seatrow']
  if (isHero) classes.push('seatrow--hero')
  if (seat.presence === 'disconnected' || seat.presence === 'idleTimeout') classes.push('seatrow--offline')
  if (hand !== null && seat.inHand && seat.handState === 'folded') classes.push('seatrow--folded')

  return (
    <li className={classes.join(' ')}>
      <span className="seatrow__no num">{seat.index + 1}</span>
      {/* 在线状态点：一扫就知道谁在。颜色只是加速读取，真正的语义在文字角标上
          （断线 / 坐出 / 离开都有对应的 badge），色弱用户不会因此漏信息。 */}
      <span className="seatrow__dot" data-presence={seat.presence} aria-hidden="true" />
      {mark !== null && (
        <span className={`seatrow__mark seatrow__mark--${mark.toLowerCase()}`} aria-label={markLabel(mark)}>
          {mark}
        </span>
      )}
      <span className="seatrow__name">
        {occupant.nickname}
        {isHero && <span className="seatrow__you">（你）</span>}
      </span>
      <span className="seatrow__badges">
        {badges.map((b) => (
          <span key={b.text} className={`badge badge--${b.tone}`}>
            {b.text}
          </span>
        ))}
      </span>
      <span className="seatrow__stack num">{formatChips(seat.stack)}</span>
      {canKick && onKick !== undefined && !isHero && (
        <button
          type="button"
          className="btn btn--kick hit-expand"
          aria-label={`移出 ${occupant.nickname}`}
          onClick={() => onKick(seat)}
        >
          移出
        </button>
      )}
    </li>
  )
}

function markLabel(mark: 'D' | 'SB' | 'BB'): string {
  return mark === 'D' ? '庄家位' : mark === 'SB' ? '小盲位' : '大盲位'
}

export default SeatList
